import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import SaveIcon from '@mui/icons-material/Save';
import {
  Button,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Modal,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/en-gb';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { LoadStatus } from '../../constants/constants';
import {
  ActivityRepeatInterval,
  ActivityStatus,
  ActivityType,
  saveActivity,
  selectActivityLoadStatus,
} from '../../redux/reducers/activityReducer';
import { selectFramework } from '../../redux/reducers/frameworkReducer';
import ControlSelector from '../ControlSelector';
import FrameworkSelector from '../FrameworkSelector';
import LoadingIndicator from '../LoadingIndicator';
import PersonSelector from '../PersonSelector';
import AddContactModal from '../organization/AddContactModal';

dayjs.extend(advancedFormat);

function ActivityModal({ handleClose, activity }) {
  const initialActivityDetails = {
    created: dayjs(Date.Date).format('X'),
    controlIds: activity.controlIds ?? [],
    frameworkId: activity.frameworkId ?? '',
    findingId: '',
    description: '',
    activityOwner: [],
    autoResolve: '',
    title: '',
    notes: '',
    activityType: ActivityType.IMPLEMENT,
    activityStatus: ActivityStatus.NOT_STARTED,
    repeatEvery: ActivityRepeatInterval.MONTH,
    recurringActivity: false,
    deadline: dayjs(Date.Date).format('X'), // Current date in UNIX format
  };
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [activityDetails, setActivityDetails] = useState(initialActivityDetails);
  const [showPersonModal, setShowPersonModal] = useState(false);
  const activityLoadStatus = useSelector(selectActivityLoadStatus);

  const handleChange = (event) => {
    const { name, value } = event.target;
    const newDetails = { ...activityDetails };
    newDetails[name] = value;
    setActivityDetails(newDetails);
    setUnsavedChanges(true);
  };

  const handleSave = () => {
    dispatch(saveActivity(activityDetails));
    handleClose();
  };

  const handleChangeDeadline = (event) => {
    setActivityDetails({ ...activityDetails, deadline: dayjs(event).format('X').toString() });
    setUnsavedChanges(true);
  };

  const handlePersonChange = (idList) => {
    setActivityDetails({ ...activityDetails, activityOwner: idList });
    setUnsavedChanges(true);
  };

  const handleFrameworkChange = (id) => {
    if (id !== activityDetails.frameworkId) {
      setActivityDetails({ ...activityDetails, frameworkId: id, controlIds: [] });
      dispatch(selectFramework(id));
      setUnsavedChanges(true);
    }
  };

  const handleControlsChange = (controls) => {
    setActivityDetails({ ...activityDetails, controlIds: controls });
    setUnsavedChanges(true);
  };

  const handleUnsavedChanges = (_, reason) => {
    if (unsavedChanges) {
      switch (reason) {
        case 'backdropClick':
          // Prevent outside click modal close
          break;
        default:
          setUnsavedChanges(false);
          handleSave();
          break;
      }
    } else {
      handleClose();
    }
  };

  const handleCancel = () => {
    handleClose();
  };

  useEffect(() => {
    if (activity.id) {
      dispatch(selectFramework(activity.frameworkId));
      const newState = { ...activity };
      setActivityDetails(newState);
    }
  }, [activity]);

  return (
    <>
      {showPersonModal && <AddContactModal handleClose={() => setShowPersonModal(false)} />}
      <Modal open onClose={handleUnsavedChanges}>
        {activityLoadStatus === LoadStatus.SUCCESS ? (
          <Container variant="modal" disableGutters>
            <Stack variant="modalTop" spacing={2}>
              <Typography variant="h6">
                {activity.id ? t('activity.modal.editActivity') : t('activity.modal.newActivity')}
              </Typography>
              <TextField
                variant="outlined"
                subtype="signinField"
                onChange={handleChange}
                name="title"
                value={activityDetails.title}
                InputLabelProps={{
                  shrink: true,
                }}
                label={t('activity.modal.title')}
                required
              />
              <TextField
                variant="outlined"
                subtype="signinField"
                onChange={handleChange}
                name="description"
                value={activityDetails.description}
                InputLabelProps={{
                  shrink: true,
                }}
                label={t('activity.modal.description')}
                required
                multiline
                minRows={6}
              />
              <Divider />
              <Stack>
                <Typography variant="overline">{t('activity.modal.relatedControls')}</Typography>
                <Typography variant="body2">{t('activity.modal.relatedControlsDescription')}</Typography>
              </Stack>
              <FrameworkSelector
                handleFrameworkChange={handleFrameworkChange}
                frameworkId={activityDetails.frameworkId}
                disabled={activityDetails.activityType === ActivityType.CORRECTIVE_ACTION}
              />
              <ControlSelector
                handleControlsChange={handleControlsChange}
                controlsList={activityDetails.controlIds}
                disabled={
                  activityDetails.activityType === ActivityType.CORRECTIVE_ACTION ||
                  activityDetails.frameworkId === ''
                }
              />
              <Divider />
              <PersonSelector
                title={t('activity.modal.activityOwner')}
                handlePersonChange={handlePersonChange}
                handleAddNew={() => setShowPersonModal(true)}
                contacts={activityDetails.activityOwner}
                fill
              />
              <FormControl>
                <FormLabel id="activity-type-label">
                  <Typography sx={{ color: 'black' }} variant="body1">
                    {`${t('activity.modal.type')} *`}
                  </Typography>
                </FormLabel>
                <RadioGroup row name="activityType" value={activityDetails.activityType} onChange={handleChange}>
                  {ActivityType.list().map((type) => (
                    <FormControlLabel
                      key={type}
                      value={type}
                      control={<Radio variant="radio" />}
                      label={t(`activity.type.${type}`)}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
              {activityDetails.activityType === ActivityType.RECURRING_OPERATIONAL_ACTIVITY && (
                <FormControl>
                  <FormLabel id="activity-type-label">
                    <Typography sx={{ color: 'black' }} variant="body1">
                      {`${t('activity.modal.repeatsEvery')} *`}
                    </Typography>
                  </FormLabel>
                  <RadioGroup row name="repeatEvery" value={activityDetails.repeatEvery} onChange={handleChange}>
                    {ActivityRepeatInterval.list().map((interval) => (
                      <FormControlLabel
                        key={interval}
                        value={interval}
                        control={<Radio variant="radio" />}
                        label={t(`activity.interval.${interval}`)}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              )}
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                <DatePicker
                  label={t('activity.modal.deadline')}
                  format="DD.MM.YYYY"
                  value={dayjs.unix(activityDetails.deadline)}
                  onChange={handleChangeDeadline}
                />
              </LocalizationProvider>
            </Stack>
            <Stack variant="modalButtons" direction="row" justifyContent="flex-end" spacing={1}>
              <Button variant="text" startIcon={<ClearIcon />} onClick={() => handleCancel()}>
                {t('activity.modal.cancel')}
              </Button>
              <Button
                disabled={!activityDetails.title || !activityDetails.description}
                variant="contained"
                startIcon={activity.id ? <SaveIcon /> : <AddIcon />}
                onClick={() => handleSave()}
              >
                {activity.id ? t('activity.modal.saveChanges') : t('activity.modal.saveNew')}
              </Button>
            </Stack>
          </Container>
        ) : (
          <LoadingIndicator />
        )}
      </Modal>
    </>
  );
}

export default ActivityModal;
